import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ExternalLink, Trash2, Copy } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { CalendarIcon } from "lucide-react";
import axios from 'axios';
import { toast } from 'react-hot-toast';

const ScheduledMeetingsList = () => {
  const [meetings, setMeetings] = useState([]);
  const [filteredMeetings, setFilteredMeetings] = useState({ upcoming: [], past: [] });
  const [mentorFilter, setMentorFilter] = useState('all');
  const [batchFilter, setBatchFilter] = useState('all');
  const [courseFilter, setCourseFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [uniqueMentors, setUniqueMentors] = useState([]);
  const [uniqueBatches, setUniqueBatches] = useState([]);
  const [uniqueCourses, setUniqueCourses] = useState([]);
  const [selectedMeetings, setSelectedMeetings] = useState([]);
  const [dateRange, setDateRange] = useState({
    from: undefined,
    to: undefined
  });

  const fetchMeetings = async () => {
    setIsLoading(true);
    setError('');

    try {
      const filters = {};
      if (mentorFilter && mentorFilter !== 'all') filters.mentorName = mentorFilter;
      if (batchFilter && batchFilter !== 'all') filters.batchName = batchFilter;
      if (courseFilter && courseFilter !== 'all') filters.courseName = courseFilter;
      
      const response = await axios.get('https://66z6lhu4ge.execute-api.ap-south-1.amazonaws.com/prod/zoom/meetings', { 
        params: filters,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const meetingsData = Array.isArray(response.data.meetings) ? response.data.meetings : [];
      setMeetings(meetingsData);
      
      if (meetingsData.length > 0) {
        // Fix: Use mentorName instead of PK for mentors
        const mentors = [...new Set(meetingsData.map(meeting => meeting.mentorName))];
        setUniqueMentors(mentors);
        
        const batches = [...new Set(
          meetingsData.flatMap(meeting => meeting.batchNames || [])
        )];
        setUniqueBatches(batches);
        
        const courses = [...new Set(meetingsData.map(meeting => meeting.courseName).filter(Boolean))];
        setUniqueCourses(courses);
      } else {
        setUniqueMentors([]);
        setUniqueBatches([]);
        setUniqueCourses([]);
        setFilteredMeetings({ upcoming: [], past: [] });
      }
    } catch (err) {
      console.error('Error fetching meetings:', err);
      setError('Failed to load scheduled meetings');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMeetings();
  }, [mentorFilter, batchFilter, courseFilter]);

  useEffect(() => {
    if (!meetings || meetings.length === 0) {
      setFilteredMeetings({ upcoming: [], past: [] });
      return;
    }
    
    const now = new Date();
    
    try {
      let filtered = [...meetings];

      // Apply date range filter if set
      if (dateRange.from || dateRange.to) {
        filtered = filtered.filter(meeting => {
          const meetingDate = new Date(meeting.meetingTime);
          if (dateRange.from && meetingDate < dateRange.from) return false;
          if (dateRange.to && meetingDate > dateRange.to) return false;
          return true;
        });
      }

      const upcoming = filtered
        .filter(meeting => {
          const meetingTime = new Date(meeting.meetingTime);
          return meetingTime >= now;
        })
        .sort((a, b) => new Date(a.meetingTime) - new Date(b.meetingTime));
      
      const past = filtered
        .filter(meeting => {
          const meetingTime = new Date(meeting.meetingTime);
          return meetingTime < now;
        })
        .sort((a, b) => new Date(b.meetingTime) - new Date(a.meetingTime));
      
      setFilteredMeetings({ upcoming, past });
    } catch (err) {
      console.error('Error filtering meetings:', err);
      setFilteredMeetings({ upcoming: [], past: [] });
    }
  }, [meetings, dateRange]);

  const handleSelectAll = (isPast) => {
    const meetingsToSelect = isPast ? filteredMeetings.past : filteredMeetings.upcoming;
    const allSelected = meetingsToSelect.every(meeting => 
      selectedMeetings.some(selected => 
        selected.mentorName === meeting.mentorName && 
        selected.meetingTime === meeting.meetingTime
      )
    );

    if (allSelected) {
      // Deselect all meetings in this category
      setSelectedMeetings(prev => 
        prev.filter(selected => 
          !meetingsToSelect.some(meeting => 
            meeting.mentorName === selected.mentorName && 
            meeting.meetingTime === selected.meetingTime
          )
        )
      );
    } else {
      // Select all meetings in this category
      const newSelected = [...selectedMeetings];
      meetingsToSelect.forEach(meeting => {
        if (!newSelected.some(selected => 
          selected.mentorName === meeting.mentorName && 
          selected.meetingTime === meeting.meetingTime
        )) {
          newSelected.push(meeting);
        }
      });
      setSelectedMeetings(newSelected);
    }
  };

  const handleSelectMeeting = (meeting) => {
    setSelectedMeetings(prev => {
      const isSelected = prev.some(selected => 
        selected.mentorName === meeting.mentorName && 
        selected.meetingTime === meeting.meetingTime
      );

      if (isSelected) {
        return prev.filter(selected => 
          !(selected.mentorName === meeting.mentorName && 
            selected.meetingTime === meeting.meetingTime)
        );
      } else {
        return [...prev, meeting];
      }
    });
  };

  const handleDeleteSelected = async () => {
    if (!selectedMeetings.length) return;

    try {
      const response = await axios.delete(
        'https://66z6lhu4ge.execute-api.ap-south-1.amazonaws.com/prod/zoom/meetings',
        {
          data: { meetings: selectedMeetings },
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 207) {
        // Some meetings failed to delete
        console.warn('Some meetings could not be deleted:', response.data.failedMeetings);
        setError(`Some meetings could not be deleted. ${response.data.successful} deleted, ${response.data.failed} failed.`);
      } else {
        // All meetings deleted successfully
        console.log('Meetings deleted successfully:', response.data);
      }

      // Clear selection and refresh the list
      setSelectedMeetings([]);
      fetchMeetings();
    } catch (error) {
      console.error('Error deleting meetings:', error);
      setError(error.response?.data?.error || 'Failed to delete selected meetings');
    }
  };

  const formatDateTime = (isoString) => {
    try {
      const date = new Date(isoString);
      return new Intl.DateTimeFormat('en-US', {
        dateStyle: 'medium',
        timeStyle: 'short'
      }).format(date);
    } catch (error) {
      return isoString;
    }
  };

  const MeetingTable = ({ meetings, isPast }) => (
    <div className="rounded-md border my-4">
      {meetings.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]">
                <Checkbox
                  checked={meetings.every(meeting => 
                    selectedMeetings.some(selected => 
                      selected.mentorName === meeting.mentorName && 
                      selected.meetingTime === meeting.meetingTime
                    )
                  )}
                  onCheckedChange={() => handleSelectAll(isPast)}
                />
              </TableHead>
              <TableHead>Mentor</TableHead>
              <TableHead>Course Name</TableHead>
              <TableHead>Date & Time</TableHead>
              <TableHead>Agenda</TableHead>
              <TableHead>Batches</TableHead>
              <TableHead>Meeting Link</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {meetings.map((meeting, index) => (
              <TableRow key={`${meeting.mentorName}-${meeting.meetingTime}-${index}`}>
                <TableCell>
                  <Checkbox
                    checked={selectedMeetings.some(selected => 
                      selected.mentorName === meeting.mentorName && 
                      selected.meetingTime === meeting.meetingTime
                    )}
                    onCheckedChange={() => handleSelectMeeting(meeting)}
                  />
                </TableCell>
                <TableCell className="font-medium">{meeting.mentorName}</TableCell>
                <TableCell className="max-w-md truncate" title={meeting.courseName}>
                  {meeting.courseName}
                </TableCell>
                <TableCell>{formatDateTime(meeting.meetingTime)}</TableCell>
                <TableCell className="max-w-md truncate" title={meeting.agenda}>
                  {meeting.agenda}
                </TableCell>
                <TableCell>
                  {meeting.batchNames && meeting.batchNames.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {meeting.batchNames.map((batch, i) => (
                        <Badge 
                          key={i} 
                          variant={isPast ? "outline" : "default"}
                        >
                          {batch}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">No batches</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <a 
                      href={meeting.zoomLink} 
                      target="_blank" 
                      rel="noreferrer"
                      className="text-blue-600 hover:text-blue-800 hover:underline flex items-center"
                    >
                      Join Meeting
                      <ExternalLink className="h-3 w-3 ml-1" />
                    </a>
                    <button
                      type="button"
                      className="ml-1 p-1 rounded hover:bg-gray-100 focus:outline-none"
                      onClick={() => {
                        navigator.clipboard.writeText(meeting.zoomLink);
                        toast.success('Link copied to clipboard!');
                      }}
                      title="Copy meeting link"
                    >
                      <Copy className="h-4 w-4 text-gray-500 hover:text-blue-600 cursor-pointer" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="p-4 text-center border border-dashed rounded-md text-muted-foreground">
          No {isPast ? "past" : "upcoming"} meetings found
        </div>
      )}
    </div>
  );

  MeetingTable.propTypes = {
    meetings: PropTypes.array.isRequired,
    isPast: PropTypes.bool.isRequired
  };

  const NoMeetingsMessage = () => (
    <div className="text-center py-8 border border-dashed rounded-lg">
      <p className="text-muted-foreground text-lg">No meetings have been scheduled yet.</p>
      <p className="text-sm text-muted-foreground mt-2">
        Schedule a meeting for mentorship batches to see them here.
      </p>
    </div>
  );

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium mb-4">Scheduled Meetings</h3>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Filters</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label htmlFor="mentor-filter">Filter by Mentor</Label>
            <Select 
              value={mentorFilter} 
              onValueChange={setMentorFilter}
            >
              <SelectTrigger id="mentor-filter" className="w-full">
                <SelectValue placeholder="All Mentors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Mentors</SelectItem>
                {uniqueMentors.map(mentor => (
                  <SelectItem key={mentor} value={mentor}>{mentor}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="batch-filter">Filter by Batch</Label>
            <Select 
              value={batchFilter} 
              onValueChange={setBatchFilter}
            >
              <SelectTrigger id="batch-filter" className="w-full">
                <SelectValue placeholder="All Batches" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Batches</SelectItem>
                {uniqueBatches.map(batch => (
                  <SelectItem key={batch} value={batch}>{batch}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="course-filter">Filter by Course</Label>
            <Select 
              value={courseFilter} 
              onValueChange={setCourseFilter}
            >
              <SelectTrigger id="course-filter" className="w-full">
                <SelectValue placeholder="All Courses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Courses</SelectItem>
                {uniqueCourses.map(course => (
                  <SelectItem key={course} value={course}>{course}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Date Range</Label>
            <div className="flex gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !dateRange.from && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateRange.from ? format(dateRange.from, "PPP") : "Start date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={dateRange.from}
                    onSelect={(date) => setDateRange(prev => ({ ...prev, from: date }))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !dateRange.to && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateRange.to ? format(dateRange.to, "PPP") : "End date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={dateRange.to}
                    onSelect={(date) => setDateRange(prev => ({ ...prev, to: date }))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Selected Button */}
      {selectedMeetings.length > 0 && (
        <div className="flex justify-end mb-4">
          <Button
            variant="destructive"
            size="sm"
            onClick={handleDeleteSelected}
            className="flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            Delete Selected ({selectedMeetings.length})
          </Button>
        </div>
      )}

      {isLoading ? (
        <div className="w-full space-y-3">
          <Skeleton className="h-[40px] w-full" />
          <Skeleton className="h-[300px] w-full" />
        </div>
      ) : error ? (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : meetings.length === 0 ? (
        <NoMeetingsMessage />
      ) : (
        <Tabs defaultValue="upcoming">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="upcoming">
              Upcoming Meetings ({filteredMeetings.upcoming.length})
            </TabsTrigger>
            <TabsTrigger value="past">
              Past Meetings ({filteredMeetings.past.length})
            </TabsTrigger>
          </TabsList>
          <TabsContent value="upcoming">
            <MeetingTable meetings={filteredMeetings.upcoming} isPast={false} />
          </TabsContent>
          <TabsContent value="past">
            <MeetingTable meetings={filteredMeetings.past} isPast={true} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default ScheduledMeetingsList; 